/**
 * Payload JSON d’une fiche page de référence (partagé UI + export rapport client).
 */

import decodeHtml from './decodeHtml';
import { buildPageFindings } from './auditFindings';
import {
  evaluateSeoCriteria,
  getTitleLengthStatus,
  getMetaLengthStatus,
} from './seoCriteria';
import { getScoreQualitative } from './scoreLabels';
import { resolveHeadingsData } from './headingsAnalysis';
import { resolveImagesList } from './imagesAudit';
import { buildPagespeedSummaryFallback } from './pagespeedSummary';
import {
  getRecommendations,
  buildVitalContextualRecommendation,
  getPriority,
  getMetricPriority,
} from './performanceRecommendations';
import {
  translateLighthouse,
  isLighthouseMetricAudit,
  translateError,
  stripMarkdown,
} from './auditDictionary';
import { isOperationalTechnicalError } from './technicalErrors';
import {
  analyzeHeadings,
  getHeadingsStructureStatus,
  deriveHeadingsCounts,
} from './headingsAnalysis';

const MAX_LIGHTHOUSE_AUDITS = 40;

/** Verdict court affiché en tête de fiche page de référence. */
export function buildPageVerdict({ highCount, total }) {
  if (highCount > 0) {
    return {
      tone: 'red',
      text: `${highCount} point${highCount > 1 ? 's' : ''} critique${highCount > 1 ? 's' : ''} à corriger en priorité.`,
      highCount,
      totalFindings: total,
    };
  }
  if (total > 0) {
    return {
      tone: 'amber',
      text: `${total} optimisation${total > 1 ? 's' : ''} recommandée${total > 1 ? 's' : ''}, rien de bloquant.`,
      highCount: 0,
      totalFindings: total,
    };
  }
  return {
    tone: 'green',
    text: 'Page saine : aucun point à corriger détecté.',
    highCount: 0,
    totalFindings: 0,
  };
}

export function mergeSeoWithPageContent(seo, page) {
  if (!seo && !page) return {};
  return {
    ...(seo || {}),
    url: seo?.url || page?.url,
    title: seo?.title || page?.title || '',
    meta_description: seo?.meta_description || page?.meta_description || '',
    h1: seo?.h1 || page?.h1 || '',
    nb_images: seo?.nb_images ?? page?.nb_images ?? 0,
    nb_images_without_alt: seo?.nb_images_without_alt,
    canonical_url: seo?.canonical_url || page?.canonical_url || '',
    score_canonical: seo?.score_canonical,
    title_length: seo?.title_length,
    meta_length: seo?.meta_length,
    nb_h1: seo?.nb_h1,
    headings: seo?.headings,
    advanced_metrics: seo?.advanced_metrics,
  };
}

function parseTodoSteps(todo) {
  if (Array.isArray(todo?.steps) && todo.steps.length > 0) {
    return todo.steps
      .map((s) => ({
        cible: String(s?.cible || s?.target || s?.element || '').trim(),
        modification: String(s?.modification || s?.change || s?.detail || s?.action || '').trim(),
      }))
      .filter((s) => s.cible || s.modification);
  }
  return [];
}

function formatAffectedResources(detailsInfo) {
  if (!detailsInfo) return '';
  const parts = [];
  for (const f of (detailsInfo.files || []).slice(0, 5)) {
    const size = f.wasted || f.size;
    parts.push(`${f.name || f.url}${size ? ` (${size} o)` : ''}`);
  }
  for (const u of (detailsInfo.urls || []).slice(0, 3)) {
    if (!parts.some((p) => p.includes(u))) parts.push(u);
  }
  return parts.join(' ; ');
}

function mapTechnicalErrors(errors) {
  return (errors || [])
    .filter((e) => !isOperationalTechnicalError(e))
    .map((err) => {
      const t = translateError(err);
      return {
        category: err.category || err.type || '',
        severity: err.severity || err.priority || 'medium',
        message: t?.label || err.message || err.category || '',
        why: t?.pourquoi || err.impact || '',
        action: t?.corriger || err.recommendation || '',
        effort: t?.effort || err.effort || 'variable',
      };
    });
}

function buildPageSpeedSection(perf, todo) {
  if (!perf?.pagespeed_average) {
    return null;
  }
  const summary = (todo?.pagespeed_summary || '').trim()
    || buildPagespeedSummaryFallback(perf);

  const vitals = [];
  const optimizations = [];
  const vitalRecommendations = [];
  if (perf.pagespeed_raw) {
    const recs = getRecommendations(perf.pagespeed_raw);
    for (const rec of recs) {
      const t = translateLighthouse(rec);
      if (!t) continue;
      if (isLighthouseMetricAudit(rec.id)) {
        const contextual = buildVitalContextualRecommendation(rec, recs);
        vitals.push({
          id: rec.id,
          label: t.label,
          displayValue: rec.displayValue || '',
          definition: t.definition || '',
          impactWhy: t.impactWhy || '',
          priority: getMetricPriority(rec.id, rec.numericValue)?.label || '',
          contextualActions: contextual.lines || [],
        });
        if (contextual.lines?.length) {
          vitalRecommendations.push({
            metric: t.label,
            lines: contextual.lines,
          });
        }
      } else if (optimizations.length < MAX_LIGHTHOUSE_AUDITS) {
        const prio = getPriority(rec.score);
        optimizations.push({
          id: rec.id,
          label: t.label,
          why: t.pourquoi || stripMarkdown(rec.description || ''),
          action: t.corriger || '',
          effort: t.effort || 'variable',
          score: rec.score,
          displayValue: rec.displayValue || '',
          priority: prio.label,
          deviceScope: rec.deviceScope || 'both',
          affectedResources: formatAffectedResources(rec.detailsInfo),
        });
      }
    }
  }

  return {
    summary,
    pagespeedSummaryIa: (todo?.pagespeed_summary || '').trim(),
    scores: {
      desktop: perf.pagespeed_performance_desktop ?? null,
      mobile: perf.pagespeed_performance_mobile ?? null,
      accessibility: perf.pagespeed_accessibility ?? null,
      bestPractices: perf.pagespeed_best_practices ?? null,
      seo: perf.pagespeed_seo > 0 ? perf.pagespeed_seo : null,
      average: perf.pagespeed_average ?? null,
    },
    error: perf.pagespeed_error || null,
    vitals,
    optimizations,
    vitalRecommendations,
  };
}

function buildActionPlan(todo) {
  if (!todo) return { summary: '', stale: false, todos: [] };
  return {
    summary: todo.summary || '',
    stale: Boolean(todo.stale),
    pagespeedSummaryIa: (todo?.pagespeed_summary || '').trim(),
    todos: (todo.todos || []).map((item) => ({
      priority: item.priority || item.priorite || '',
      title: item.title || item.tache || '',
      description: item.description || item.detail || '',
      why: item.why || '',
      category: item.category || '',
      effort: item.effort || '',
      steps: parseTodoSteps(item),
    })),
  };
}

/**
 * @param {{ url: string }} page
 * @param {object|null} seo
 * @param {object|null} perf
 * @param {Array} errors
 * @param {object|null} todo
 */
export function buildReferencePageExportEntry(page, seo, perf, errors, todo) {
  const mergedSeo = mergeSeoWithPageContent(seo, page);
  const criteria = evaluateSeoCriteria(mergedSeo);
  const title = decodeHtml(mergedSeo?.title || '');
  const meta = decodeHtml(mergedSeo?.meta_description || '');
  const h1Text = decodeHtml(mergedSeo?.h1 || '');
  const titleStatus = getTitleLengthStatus(criteria.titleLen, !!mergedSeo?.title);
  const metaStatus = getMetaLengthStatus(criteria.metaLen, !!meta);
  const { headingsList, headingsCounts: rawCounts } = resolveHeadingsData(mergedSeo, h1Text);
  const headingsCounts = rawCounts && Object.keys(rawCounts).length
    ? rawCounts
    : deriveHeadingsCounts(headingsList);
  const headingsStructure = getHeadingsStructureStatus(headingsList, headingsCounts);
  const headingsAnalysis = analyzeHeadings(headingsList, headingsCounts);
  const imagesList = resolveImagesList(mergedSeo);

  const pageFindings = buildPageFindings({
    url: page.url,
    seo: mergedSeo,
    perf,
    errors,
  });
  const highCount = pageFindings.filter((f) => f.severity === 'high').length;
  const verdict = buildPageVerdict({ highCount, total: pageFindings.length });

  const seoScore = Math.round(Number(mergedSeo?.score_global) || 0);
  const perfScore = Math.round(Number(perf?.pagespeed_average) || 0);

  return {
    url: page.url,
    verdict,
    scores: {
      seo: seoScore > 0 ? seoScore : null,
      performance: perfScore > 0 ? perfScore : null,
      labels: {
        seo: seoScore > 0 ? getScoreQualitative(seoScore).label : null,
        performance: perfScore > 0 ? getScoreQualitative(perfScore).label : null,
      },
    },
    seoContent: {
      title,
      metaDescription: meta,
      h1: h1Text,
      canonicalUrl: mergedSeo?.canonical_url || '',
      criteria: {
        titleOk: criteria.titleOk,
        metaOk: criteria.metaOk,
        h1Ok: criteria.h1Ok,
        imagesOk: criteria.imagesOk,
        canonOk: criteria.canonOk,
        titleLen: criteria.titleLen,
        metaLen: criteria.metaLen,
        h1Count: criteria.h1Count,
        nbImages: criteria.nbImages,
        nbWithoutAlt: criteria.nbWithoutAlt,
      },
      titleStatus: {
        label: titleStatus.label,
        tone: titleStatus.tone,
        recommendation: titleStatus.recommendation || null,
      },
      metaStatus: {
        label: metaStatus.label,
        tone: metaStatus.tone,
        recommendation: metaStatus.recommendation || null,
      },
      headingsStructure: {
        label: headingsStructure.label,
        tone: headingsStructure.tone,
        counts: headingsCounts,
      },
      headingsAnalysis: {
        issues: (headingsAnalysis.issues || []).map((i) => ({
          severity: i.severity,
          message: i.message,
          fix: i.fix || '',
        })),
        strengths: headingsAnalysis.strengths || [],
        contentRecommendations: (headingsAnalysis.contentRecs || []).map((r) => ({
          message: r.message,
          suggestion: r.suggestion || '',
        })),
      },
      headings: headingsList.map((h) => ({
        level: h.level || (h.tag ? parseInt(String(h.tag).replace(/\D/g, ''), 10) : '') || h.tag || '',
        text: decodeHtml(h.text || h.content || ''),
      })),
      images: imagesList.map((img) => ({
        src: img.src || img.url || '',
        alt: img.alt || '',
        hasAlt: img.has_alt ?? Boolean(img.alt?.trim()),
      })),
      imagesSummary: {
        total: criteria.nbImages,
        withoutAlt: criteria.nbWithoutAlt,
      },
    },
    technicalErrors: mapTechnicalErrors(errors),
    findings: pageFindings.map((f) => ({
      pilier: f.pilier,
      severity: f.severity,
      effort: f.effort,
      source: f.source,
      title: f.title,
      why: f.why,
      action: f.action,
    })),
    pageSpeed: buildPageSpeedSection(perf, todo),
    actionPlan: buildActionPlan(todo),
  };
}
